import { ref, computed, watch } from 'vue'
import type { Product } from '@scayle/storefront-nuxt'
import { useCookie } from '#app'
import {
  DEFAULT_PRICE_PROMOTION_KEY,
  DEFAULT_PRODUCT_WITH,
} from '../constants/withParameters'
import { useRpc } from '#storefront/composables'

const MAX_PRODUCTS = 16

export const LAST_SEEN_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

export const useLastSeenProducts = (productId?: number) => {
  const cookieName = 'last_seen_products'
  const lastSeenCookie = useCookie<number[]>(cookieName, {
    maxAge: LAST_SEEN_COOKIE_MAX_AGE_SECONDS,
  })
  const lastSeenProducts = ref<Product[]>([])
  const isLoading = ref(false)
  const ids = computed(() => lastSeenCookie.value || [])

  const addProductIdToLastSeenCookie = (productId: number) => {
    const existing = lastSeenCookie.value || []
    const updated = [productId, ...existing.filter((id) => id !== productId)]
    lastSeenCookie.value = updated.slice(0, MAX_PRODUCTS)
  }

  const fetchLastSeenProducts = async () => {
    const filteredIds =
      productId != null ? ids.value.filter((id) => id !== productId) : ids.value

    if (!filteredIds.length) {
      lastSeenProducts.value = []
      return
    }

    isLoading.value = true
    try {
      const cacheKey = `last_seen_${ids.value.join('-')}`

      const rpcData = await useRpc('getProductsByIdsParallel', cacheKey, {
        productIds: filteredIds,
        with: DEFAULT_PRODUCT_WITH,
        pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
      })

      const fetched = rpcData.data?.value || []
      const productsById = Object.fromEntries(fetched.map((p) => [p.id, p]))

      lastSeenProducts.value = filteredIds.reduce<Product[]>((acc, id) => {
        const product = productsById[id]
        if (product) acc.push(product)
        return acc
      }, [])
    } finally {
      isLoading.value = false
    }
  }

  if (import.meta.client) {
    fetchLastSeenProducts()
  }

  // fetch products whenever the cookie changes
  // watch(ids, async () => await fetchLastSeenProducts(), { immediate: true })

  return {
    lastSeenProducts,
    addProductIdToLastSeenCookie,
    fetchLastSeenProducts,
    isLoading,
  }
}
